// The landscape: a few thousand grass points, vector hex bricks and tree spots. The ground itself is flat.
// World units are grid units (read as metres on the plan). gx runs right-down, gy left-down, gz up.

import { PROJECTS } from './projects';

export const PLOT_W = 100;
export const PLOT_D = 70;

function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const sstep = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

// ---- places --------------------------------------------------------------------
export interface Place {
  id: string; x: number; y: number;
  pad: number; // radius kept clear of grass
  plaza: number; // radius of paving around it (0 = none)
  top: number; // height of the pin anchor above ground
}
export const PLACES: Place[] = [
  { id: 'field', x: 80, y: 58, pad: 6, plaza: 0, top: 4.6 },
  { id: 'well', x: 68, y: 46, pad: 9, plaza: 5.5, top: 5.4 },
  { id: 'greenhouse', x: 38, y: 58, pad: 15, plaza: 11, top: 8.2 },
  { id: 'desk', x: 8, y: 46, pad: 12, plaza: 8.5, top: 5 },
  // the three design chapters: each a hub that spreads into a ring of project icons around it.
  // The chapter's own place only anchors its marker; the structures are the projects below.
  { id: 'architecture', x: 24, y: 12, pad: 6, plaza: 4.5, top: 8 },
  { id: 'planning', x: 54, y: 14, pad: 6, plaza: 4.5, top: 8 },
  { id: 'interviews', x: 86, y: 10, pad: 6, plaza: 4.5, top: 8 },
  ...PROJECTS.map((p) => ({ id: p.id, x: p.x, y: p.y, pad: 3.2, plaza: 0, top: 5 })),
];
const distTo = (p: Place, x: number, y: number): number => Math.hypot(p.x - x, p.y - y);

// ---- the path ------------------------------------------------------------------
const CTRL: [number, number][] = [
  [100, 62], [90, 60], [80, 58], [72, 50], [68, 46], [56, 54], [46, 58], [38, 58], [24, 54], [10, 50], [8, 46],
  [10, 34], [18, 20], [24, 12], [38, 10], [54, 14], [70, 12], [86, 10],
];
function catmull(p0: number, p1: number, p2: number, p3: number, t: number): number {
  return 0.5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t + (-p0 + 3 * p1 - 3 * p2 + p3) * t * t * t);
}
// One path winds through every place in order, from the gate across the whole plot, as if worn by walking.
const PATH: [number, number][] = [];
for (let i = 0; i < CTRL.length - 1; i++) {
  const p0 = CTRL[Math.max(0, i - 1)], p1 = CTRL[i], p2 = CTRL[i + 1], p3 = CTRL[Math.min(CTRL.length - 1, i + 2)];
  for (let k = 0; k < 40; k++) {
    const t = k / 40;
    PATH.push([catmull(p0[0], p1[0], p2[0], p3[0], t), catmull(p0[1], p1[1], p2[1], p3[1], t)]);
  }
}
PATH.push(CTRL[CTRL.length - 1]);
export function distToPath(x: number, y: number): number {
  let d = 1e9;
  for (const p of PATH) {
    const dx = p[0] - x, dy = p[1] - y;
    const v = dx * dx + dy * dy;
    if (v < d) d = v;
  }
  return Math.sqrt(d);
}

// ---- hex bricks ----------------------------------------------------------------
/** A honeycomb whose bricks nearly touch, with uneven joints and the odd missing brick. One row per brick. */
export interface Bricks { n: number; x: Float32Array; y: Float32Array; r: Float32Array; col: Uint8Array }
export function generateBricks(): Bricks {
  const r = rng(11);
  const HR = 0.95;
  const hw = Math.sqrt(3) * HR;
  const xs: number[] = [], ys: number[] = [], rs: number[] = [], cs: number[] = [];
  for (let row = -1; row < PLOT_D / (1.5 * HR) + 1; row++) {
    for (let col = -1; col < PLOT_W / hw + 1; col++) {
      const cx = col * hw + (row & 1) * hw * 0.5;
      const cy = row * 1.5 * HR;
      if (cx < 0 || cx > PLOT_W || cy < 0 || cy > PLOT_D) continue;
      const onPath = distToPath(cx, cy) < 2.3;
      const inPlaza = PLACES.some((p) => p.plaza > 0 && distTo(p, cx, cy) < p.plaza);
      if (!onPath && !inPlaza) continue;
      if (r() < 0.035) continue;
      xs.push(cx + (r() - 0.5) * 0.08);
      ys.push(cy + (r() - 0.5) * 0.08);
      rs.push(HR * (1 - (0.03 + 0.16 * Math.pow(r(), 2.4))));
      cs.push((row + col) & 1);
    }
  }
  return { n: xs.length, x: Float32Array.from(xs), y: Float32Array.from(ys), r: Float32Array.from(rs), col: Uint8Array.from(cs) };
}

// ---- grass ---------------------------------------------------------------------
export interface Grass {
  n: number;
  x: Float32Array; y: Float32Array; z: Float32Array;
  hf: Float32Array; // height fraction along the blade, 0..1
  ph: Float32Array; // wind phase
  start: Int32Array; // three opacity tiers: tier t spans start[t]..start[t+1]
}

/** Tall wild grass: tufts of blades, each blade a short stack of points. Clear round the places and the path. */
export function generateGrass(lite: boolean): Grass {
  const r = rng(7);
  const xs: number[] = [], ys: number[] = [], zs: number[] = [], hs: number[] = [], ps: number[] = [], ts: number[] = [];
  const tufts = lite ? 620 : 1250;
  for (let i = 0; i < tufts; i++) {
    const x = r() * PLOT_W, y = r() * PLOT_D;
    let hMul = sstep(1.6, 4.2, distToPath(x, y));
    for (const p of PLACES) hMul = Math.min(hMul, sstep(p.pad * 0.9, p.pad * 1.5, distTo(p, x, y)));
    if (hMul < 0.05) continue;
    const H = (1.5 + 1.9 * Math.pow(r(), 0.8)) * (0.3 + 0.7 * hMul);
    for (let bl = 0; bl < 3; bl++) {
      const lx = (r() - 0.5) * 0.5, ly = (r() - 0.5) * 0.5;
      const s = r();
      const tier = s < 0.4 ? 0 : s < 0.72 ? 1 : 2;
      const ph = r() * 6.283;
      for (const f of [0.38, 0.7, 1]) {
        xs.push(x + lx * f * H); ys.push(y + ly * f * H); zs.push(H * f); hs.push(f); ps.push(ph); ts.push(tier);
      }
    }
  }
  const n = xs.length;
  const order = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [order[i], order[j]] = [order[j], order[i]]; }
  order.sort((a, b) => ts[a] - ts[b]); // stable, so the shuffle survives inside each tier
  const g: Grass = {
    n, start: new Int32Array(4),
    x: new Float32Array(n), y: new Float32Array(n), z: new Float32Array(n), hf: new Float32Array(n), ph: new Float32Array(n),
  };
  order.forEach((i, k) => {
    g.x[k] = xs[i]; g.y[k] = ys[i]; g.z[k] = zs[i]; g.hf[k] = hs[i]; g.ph[k] = ps[i];
    g.start[ts[i] + 1]++;
  });
  for (let t = 0; t < 3; t++) g.start[t + 1] += g.start[t];
  return g;
}
